import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Container,
  Section,
  SectionHead,
  Card,
  ComLifecycle,
  buttonStyles,
  fadeUp,
  stagger,
  inView,
} from "@comai-portal/design-system";

/**
 * ComsPage — the currency for agents. (CO 069)
 *
 * The most novel thing ComOS has, given its own destination. Corpus: a Com is
 * perishable, single-use, 1¢ pegged to the dollar, a verb not a noun — built
 * for an actor that wants to ACT, not hold. Threads to /pricing (how it's
 * priced) and /trust.
 */
export function ComsPage() {
  return (
    <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
      {/* Hero */}
      <Section>
        <Container width="prose" className="text-center">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.p variants={fadeUp} className="ds-eyebrow mb-5">
              Coms — the currency for agents
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-display font-semibold tracking-[-0.02em] text-[clamp(2rem,5.5vw,3.75rem)] leading-[1.08] text-balance"
            >
              A currency built for things that act, not things that hold.
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-lead text-mute dark:text-mute-soft mt-6"
            >
              An agent doesn't want a wallet. It wants to do the next thing —
              buy the label, send the email, make the purchase — and move on. A
              Com is made for exactly that: a single, perishable unit of action,
              spent once and gone.
            </motion.p>
          </motion.div>
        </Container>
      </Section>

      {/* The lifecycle — made, spent, destroyed */}
      <Section tint="sunk">
        <Container>
          <SectionHead
            align="center"
            eyebrow="What a Com is"
            title="Made. Spent once. Destroyed."
            lead="Like a subway token that dissolves the moment you push through the turnstile — or a ticket the usher tears as you walk in. It does one job, then it's gone."
          />
          <motion.div
            {...inView}
            variants={stagger}
            className="mt-12 grid gap-5 md:grid-cols-3"
          >
            {[
              {
                n: "1",
                k: "Made",
                v: "A tenant turns a dollar into Coms. Each Com is backed 1:1 by a real dollar held in reserve — pegged to the dollar, worth one cent.",
              },
              {
                n: "2",
                k: "Spent",
                v: "An agent spends a Com to take a real action. It's redeemed back into a dollar for whoever earned it, at the moment of the sale.",
              },
              {
                n: "3",
                k: "Destroyed",
                v: "Once spent, the Com is gone. No second use. No balance that sits and grows. Unspent Coms expire — 90 days at most — and their backing becomes a claim that returns to you.",
              },
            ].map((it) => (
              <motion.div key={it.k} variants={fadeUp}>
                <Card className="h-full">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-signal text-white font-grotesk text-sm font-semibold">
                      {it.n}
                    </span>
                    <h3 className="text-display-sm">{it.k}</h3>
                  </div>
                  <p className="text-mute dark:text-mute-soft text-sm">
                    {it.v}
                  </p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
          <motion.div {...inView} variants={fadeUp} className="mt-12">
            <Card className="max-w-3xl mx-auto">
              <ComLifecycle />
            </Card>
          </motion.div>
        </Container>
      </Section>

      {/* One economy — the FofF invariant on the money page (CO 494 Phase 4).
          The network recurses in sovereignty; the Com never does. Money truth
          lives at the hub and projects down read-only (CO 495, enforced). */}
      <Section>
        <Container width="prose" className="text-center">
          <motion.div {...inView} variants={fadeUp}>
            <p className="ds-eyebrow mb-5">One economy</p>
            <h2 className="text-display-md text-balance">
              One currency, no matter how deep the tree.
            </h2>
            <p className="text-lead text-mute dark:text-mute-soft mt-6">
              ComOS is a federation of federations: every business runs a world
              of its own — in our cloud or on hardware it owns — and every
              world spends and earns the same Com. One mint, one ledger, one
              settlement rail. Worlds govern themselves; the money never forks.
              A node on your own machine cannot write the ledger, by
              construction — which is what lets your world trade with every
              other and trust the record.
            </p>
          </motion.div>
        </Container>
      </Section>

      {/* The rails — money in (card or stablecoin), money out (on-chain USDC).
          Raised from the shelf-life card to its own section (Ron 2026-09-06:
          "The Com page mentions Stripe, but i didnt find anything about
          StableCoin"). Every claim here restates the live-proven shelf-life
          card + the rail-adapter framing from the fee canon. */}
      <Section tint="sunk">
        <Container>
          <SectionHead
            align="center"
            eyebrow="The rails"
            title="Dollars in. Stablecoin in. USDC out."
            lead="The Com sits between the money worlds and belongs to neither. Rails are adapters at the gates — and the cut is identical on every rail."
          />
          <motion.div
            {...inView}
            variants={stagger}
            className="mt-12 grid gap-5 md:grid-cols-3"
          >
            {[
              {
                k: "Money in, two rails",
                v: "Fund with dollars by card through Stripe, or with stablecoin straight from your own wallet. ComOS keeps the two rails separate, always — dollars and stablecoin never mix on the books.",
              },
              {
                k: "Money out, on-chain",
                v: "Redemptions, vendor payouts, and the house's own settlement move as real USDC on-chain — live-proven with real money, books closed solvent.",
              },
              {
                k: "The Com is the invariant",
                v: "Whatever rail the value rides, what gets minted is the same Com, backed 1:1. A new rail is a new adapter at the gate — never a new fee, never a new architecture.",
              },
            ].map((it) => (
              <motion.div key={it.k} variants={fadeUp}>
                <Card hover className="h-full">
                  <h3 className="text-display-sm mb-2">{it.k}</h3>
                  <p className="text-mute dark:text-mute-soft text-sm">
                    {it.v}
                  </p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </Section>

      {/* Why perishable matters */}
      <Section>
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <SectionHead
              eyebrow="Why perishable"
              title="No hoarding. No speculation. No balance to game."
              lead="Because a Com can't pile up, our cut rides your actual spending — transaction by transaction, never a flat guess, never a charge for money you're holding. Perishability is velocity taken to its limit: a Com exists for exactly one transaction."
            />
            <motion.div {...inView} variants={stagger} className="grid gap-4">
              {[
                "Pegged to the dollar — worth a cent, backed by a real dollar in reserve.",
                "A verb, not a noun — used, not owned; spent, not stored.",
                "A shelf life, not a vault — every Com expires within 90 days; unspent backing comes back as a claim you collect.",
                "Legible to an agent — a value it can compute against, not a managed rate it has to trust.",
                "Spent under your rules — caps and autonomy you set; use without custody.",
              ].map((t) => (
                <motion.div key={t} variants={fadeUp}>
                  <Card className="text-sm">{t}</Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </Container>
      </Section>

      {/* Not a store of value — the shelf life */}
      <ShelfLife />

      {/* Why a tenant, not just stablecoin? */}
      <WhyATenant />

      {/* Close — thread to pricing + trust */}
      <Section tint="ink">
        <Container className="text-center">
          <motion.div
            {...inView}
            variants={fadeUp}
            className="max-w-2xl mx-auto"
          >
            <h2 className="text-display-lg text-balance text-paper">
              You don't buy a Com until the work needs one.
            </h2>
            <p className="text-lead text-paper/70 mt-6">
              Coms are how your business pays for what it does — and how our cut
              rides the flow instead of charging you to exist.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/pricing"
                className={buttonStyles({ variant: "primary", size: "lg" })}
              >
                See how it's priced
              </Link>
              <Link
                to="/trust"
                className={buttonStyles({
                  variant: "outline",
                  size: "lg",
                  className:
                    "border-paper/25 text-paper hover:text-white hover:border-paper",
                })}
              >
                Why you can trust the peg
              </Link>
            </div>
          </motion.div>
        </Container>
      </Section>
    </div>
  );
}

/* ── Not a store of value — the shelf life ────────────────────────────
 * The quality that makes a Com a lubricant for exchange rather than a
 * place to park value. Every claim here is live-proven mechanism: expiry
 * stamped at mint, an automatic sweep, books closed solvent (CO 315), and
 * — proven with real money 2026-08-10 (CO 441) — on-demand redemption to
 * real on-chain USDC plus expiry-to-claimable-payable, drained on-chain.
 * CO 444 (2026-08-10): execution is self-serve — the redeem call books the
 * claim on the keyless gateway and a key-holding executor pays it.
 */
const SHELF_LIFE = [
  {
    k: "A hard expiry, on every Com",
    v: "Every Com is stamped with a shelf life the moment it's made — 45 days by default, never more than 90. There is no immortal Com. Value can move through ComOS; it cannot settle into it.",
  },
  {
    k: "Convert back — proven with real money",
    v: "Unspent backing redeems into the money that made it, self-serve. Proven live: real Coms redeemed into real on-chain USDC, a replayed request answered with the same transaction instead of a second send, and the books closed solvent. The redeem call books your claim on a keyless gateway; a dedicated key-holding executor pays it within minutes.",
  },
  {
    k: "Expiry is a return ticket, not a forfeiture",
    v: "When an unspent Com reaches its shelf life, an automatic sweep retires it and its backing becomes a claim you collect — everything except the mint cut you already paid on the way in. Proven live: a real expired lot's backing came back as real USDC. No breakage, no gift-card trap.",
  },
  {
    k: "Dollars and stablecoin never mix",
    v: "Fund with dollars through Stripe or with stablecoin from your own wallet — ComOS keeps the two rails separate, always. And value leaves the way it arrived: redemptions, vendor payouts, and the house's own settlement all move as real USDC on-chain, live-proven.",
  },
];

function ShelfLife() {
  return (
    <Section tint="sunk">
      <Container>
        <SectionHead
          align="center"
          eyebrow="Not a store of value"
          title="Every Com has a shelf life."
          lead="An agent doesn't need a savings account — it needs to act. So a Com can't become one. It's a lubricant for exchange: value that stops moving goes home on its own. Nothing you put into ComOS can quietly become our float."
        />
        <motion.div
          {...inView}
          variants={stagger}
          className="mt-12 grid gap-5 md:grid-cols-2"
        >
          {SHELF_LIFE.map((it) => (
            <motion.div key={it.k} variants={fadeUp}>
              <Card accent hover className="h-full">
                <h3 className="text-display-sm mb-2">{it.k}</h3>
                <p className="text-mute dark:text-mute-soft text-sm">{it.v}</p>
              </Card>
            </motion.div>
          ))}
        </motion.div>
        <motion.p
          {...inView}
          variants={fadeUp}
          className="mt-10 text-center text-sm text-mute dark:text-mute-soft max-w-2xl mx-auto"
        >
          Not a promise — a mechanism, verified live with real money: a real
          Com expired in production, a real redemption paid out as on-chain
          USDC, and the ledger closed solvent with zero unbalanced entries.
          Your exposure is bounded to the float you choose to mint — and every
          Com in that float carries a return ticket.
        </motion.p>
      </Container>
    </Section>
  );
}

/* ── Why a tenant, not just stablecoin? ───────────────────────────────
 * The sharpest objection from a sophisticated operator. The honest answer
 * doesn't argue the Com is better money (it's pegged 1:1, it isn't) — it
 * ABSORBS the rail (you can already fund in stablecoin) and argues the OS.
 * Then: 3 capabilities a currency can't give you + 3 protections a wallet
 * doesn't have, led by scoped-credential-vs-drainable-key. (CO 032-004)
 */
const CAPABILITIES = [
  {
    k: "A governed agent fleet",
    v: "Stablecoin moves money; it doesn't run your business. ComOS gives you dozens of agents acting on your behalf, under autonomy limits you set. A raw currency has no agents.",
  },
  {
    k: "Demand from the whole network",
    v: "A tenant is found by every AI that shops the federated network. Stablecoin gives you a wallet; ComOS gives you reach — agents that buy from you with no integration work.",
  },
  {
    k: "One ledger, both economies",
    v: "One tenant, one set of books — your agent spend and your human sales settled and reconciled together. Stablecoin is a rail; ComOS is the bookkeeping around it.",
  },
];

const PROTECTIONS = [
  {
    k: "A scoped credential, not a drainable key",
    v: "An agent holding a stablecoin private key can empty the wallet — there's no smaller permission to grant. On ComOS an agent acts under a scoped, capped, revocable credential (OAuth 2.1 + PKCE, per-agent scopes, SSO, per-tenant isolation). The blast radius of a bad agent is bounded by design.",
  },
  {
    k: "Governed, reversible actions",
    v: "Stablecoin transactions are irreversible — a wrong move is gone. ComOS gates the big moves behind your approval, confidence-scores every decision, logs a full audit trail, and supports time-windowed rollback.",
  },
  {
    k: "Trust you can verify",
    v: "No currency tells you whether its issuer is honest. ComOS hands you the evidence — QC on every change, an open change record, a live honesty rate. Trustworthiness as a mechanism, visible to you.",
  },
];

function WhyATenant() {
  return (
    <Section>
      <Container>
        <SectionHead
          align="center"
          eyebrow="Why a tenant, not just a wallet"
          title="You could run a business on stablecoin alone. Here's what that leaves on the table."
          lead="The honest part first: you can already fund in stablecoin — inside ComOS. The Com is the invariant; the inbound rail is just an adapter. So the currency was never the point. The currency is the cheap part. The OS is the value."
        />

        <motion.p
          {...inView}
          variants={fadeUp}
          className="mt-12 mb-5 text-center ds-eyebrow"
        >
          Three things a currency can't give you
        </motion.p>
        <motion.div
          {...inView}
          variants={stagger}
          className="grid gap-5 md:grid-cols-3"
        >
          {CAPABILITIES.map((it) => (
            <motion.div key={it.k} variants={fadeUp}>
              <Card hover className="h-full">
                <h3 className="text-display-sm mb-2">{it.k}</h3>
                <p className="text-mute dark:text-mute-soft text-sm">{it.v}</p>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        <motion.p
          {...inView}
          variants={fadeUp}
          className="mt-12 mb-5 text-center ds-eyebrow"
        >
          Three protections a wallet doesn't have
        </motion.p>
        <motion.div
          {...inView}
          variants={stagger}
          className="grid gap-5 md:grid-cols-3"
        >
          {PROTECTIONS.map((it) => (
            <motion.div key={it.k} variants={fadeUp}>
              <Card accent hover className="h-full">
                <h3 className="text-display-sm mb-2">{it.k}</h3>
                <p className="text-mute dark:text-mute-soft text-sm">{it.v}</p>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </Container>
    </Section>
  );
}
